/**
 * InvoiceCard — renders one membership-fee invoice as a labelled field stack
 * (T028 support component).
 *
 * Mirrors the proto's per-invoice card (Jasenmaksut.tsx `renderInvoiceCard`):
 *   - Always: invoice number, amount, due date, status.
 *   - When the invoice carries them: account number (+ copy), bank, reference
 *     (+ copy), and the invoice date (not in the proto; customer ruling D-252(a),
 *     D-PD-014). Billing method only on the latest invoice. The demo port scopes
 *     the banking fields to the latest unpaid invoice as the proto does; the real
 *     path carries them per invoice. This component renders whatever the invoice
 *     carries.
 *
 * Amounts arrive as euro decimal strings ("24.00"); they render localised as
 * "24,00 €" (fi) / "€24.00" (en). Dates arrive ISO (YYYY-MM-DD) and render
 * via local-calendar formatting (RD-007).
 *
 * No raw colour literals (FR-013) — token classes only. Mobile-first.
 *
 * Owned by feature 026-proto-convergence-v2.
 * Task: T028
 */

import type { JSX, ReactNode, Ref } from 'react';
import { useTranslate } from '../../i18n/useTranslate.js';
import type { Invoice } from '../../membership-billing/MembershipBillingPort.js';
import {
  BuildingIcon,
  CalendarIcon,
  CheckIcon,
  CopyIcon,
  CreditCardIcon,
  EuroIcon,
  HashIcon,
  LandmarkIcon,
} from '../../shell/icons.js';

export interface InvoiceCardProps {
  readonly invoice: Invoice;
  readonly locale: string;
  /** Copy handler keyed by what was copied, so the card can show per-row feedback. */
  readonly onCopy: (value: string, key: string) => void;
  readonly copiedKey: string | null;
  /**
   * The member's delivery method, rendered as the card's last row — the proto's
   * placement (`Laskutustapa - only for first/latest invoice`), so it is passed
   * ONLY for the latest invoice.
   *
   * The VALUE is a fact about the member, not about this invoice: since 048 it
   * comes from the `GET /v1/invoices` envelope, not from `Invoice`. The card
   * renders it where the proto put it without pretending to own it, which is why
   * it arrives as a prop rather than a field on `invoice`.
   */
  readonly deliveryMethod?: {
    readonly label: string;
    /** The proto's inline "Tilaa e-laskuehdotus" affordance, on the same row. */
    readonly action?: JSX.Element | null;
    /** Focus target for the method value, when a re-read makes it the answer (048). */
    readonly labelRef?: Ref<HTMLSpanElement>;
  };
}

function formatAmount(amount: string, locale: string): string {
  const value = Number(amount);
  if (Number.isNaN(value)) {
    // Defensive: surface the raw string rather than "NaN" if the port ever
    // sends a non-numeric amount.
    return amount;
  }
  return new Intl.NumberFormat(locale === 'en' ? 'en-GB' : 'fi-FI', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
}

function formatDate(dateString: string, locale: string): string {
  const [y, m, d] = dateString.split('-').map(Number) as [number, number, number];
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
    // Defensive: surface the raw string rather than "Invalid Date" if the port
    // ever sends a malformed ISO date.
    return dateString;
  }
  const date = new Date(y, m - 1, d);
  return new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : 'fi-FI', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  }).format(date);
}

// A blank value is absent: rendering it would leave an empty row (D-252(a)).
function hasText(value: string | undefined): value is string {
  return value !== undefined && value.trim() !== '';
}

// Proto invoice rows carry a leading field icon (Jasenmaksut.tsx:146-168 at pin
// 9393e6f8). Token-tinted via currentColor; the glyph is decorative and sits
// inside the <dt> as its first child (so the grouping <div> holds only <dt>/<dd>
// — axe definition-list, PR #282), preceding the label so it reads as a true
// leading icon (distinct from the trailing copy button in the value row).
const fieldIconClasses = 'h-5 w-5 shrink-0 text-muted-foreground';

function Row({
  icon,
  label,
  children,
}: {
  icon: JSX.Element;
  label: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="border-b border-border px-4 py-3 last:border-b-0">
      {/* Grouping <div> children must be only <dt>/<dd> (axe definition-list /
          only-dlitems, PR #282): the leading icon lives inside the <dt>. The
          <dd> keeps its flex row for the trailing copy button. */}
      <dt className="flex items-center gap-3 text-sm font-medium text-muted-foreground">
        {icon}
        {label}
      </dt>
      <dd className="mt-1 flex items-center justify-between gap-2 pl-8 text-sm text-foreground">
        {children}
      </dd>
    </div>
  );
}

function CopyButton({
  label,
  copied,
  onClick,
}: {
  label: string;
  copied: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1 rounded-md px-2 py-1 transition-colors hover:bg-muted"
      aria-label={label}
      title={label}
    >
      {copied ? (
        <CheckIcon className="h-4 w-4 text-green-600" aria-hidden="true" />
      ) : (
        <CopyIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
      )}
    </button>
  );
}

export function InvoiceCard({
  invoice,
  locale,
  onCopy,
  copiedKey,
  deliveryMethod,
}: InvoiceCardProps): JSX.Element {
  const t = useTranslate();
  // 044 US3: the overlay display label wins when present. `optimistic-paid`
  // shows the DISCLOSED "Maksettu (Paytrail)" (honest shadow — not authoritative);
  // `authoritative-paid` the plain "Maksettu"; `unavailable` the honest
  // temporarily-unavailable (FR-006a). Absent → fall back to the three honest 041
  // arms (paid / unpaid / status-unavailable), never coerced to paid/unpaid.
  const label =
    invoice.paidLabel ??
    (invoice.status === 'paid'
      ? 'authoritative-paid'
      : invoice.status === 'unpaid'
        ? 'unpaid'
        : 'unavailable');
  const statusLabel =
    label === 'authoritative-paid'
      ? t('profile.fee.statusPaid')
      : label === 'optimistic-paid'
        ? t('profile.fee.statusPaidPaytrail')
        : label === 'unpaid'
          ? t('profile.fee.statusUnpaid')
          : t('profile.fee.statusUnavailable');
  const statusClass =
    label === 'authoritative-paid' || label === 'optimistic-paid'
      ? 'font-medium text-green-600'
      : label === 'unpaid'
        ? 'font-medium text-orange-600'
        : 'font-medium text-muted-foreground';
  const accountCopyKey = `account-${invoice.id}`;
  const referenceCopyKey = `reference-${invoice.id}`;

  return (
    <dl className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card">
      <Row
        icon={<HashIcon className={fieldIconClasses} aria-hidden="true" />}
        label={t('profile.fee.invoiceNumber')}
      >
        <span>{invoice.number}</span>
      </Row>

      {hasText(invoice.accountNumber) && (
        <Row
          icon={<LandmarkIcon className={fieldIconClasses} aria-hidden="true" />}
          label={t('profile.fee.account')}
        >
          <span>{invoice.accountNumber}</span>
          <CopyButton
            label={t('profile.fee.copyAccount')}
            copied={copiedKey === accountCopyKey}
            // biome-ignore lint/style/noNonNullAssertion: guarded by the enclosing conditional
            onClick={() => onCopy(invoice.accountNumber!, accountCopyKey)}
          />
        </Row>
      )}

      {hasText(invoice.bank) && (
        <Row
          icon={<BuildingIcon className={fieldIconClasses} aria-hidden="true" />}
          label={t('profile.fee.bank')}
        >
          <span>{invoice.bank}</span>
        </Row>
      )}

      {invoice.paymentReference !== undefined && (
        <Row
          icon={<HashIcon className={fieldIconClasses} aria-hidden="true" />}
          label={t('profile.fee.reference')}
        >
          <span>{invoice.paymentReference}</span>
          <CopyButton
            label={t('profile.fee.copyReference')}
            copied={copiedKey === referenceCopyKey}
            // biome-ignore lint/style/noNonNullAssertion: guarded by the enclosing conditional
            onClick={() => onCopy(invoice.paymentReference!, referenceCopyKey)}
          />
        </Row>
      )}

      <Row
        icon={<EuroIcon className={fieldIconClasses} aria-hidden="true" />}
        label={t('profile.fee.amount')}
      >
        <span>{formatAmount(invoice.amount, locale)}</span>
      </Row>

      {hasText(invoice.invoiceDate) && (
        <Row
          icon={<CalendarIcon className={fieldIconClasses} aria-hidden="true" />}
          label={t('profile.fee.invoiceDate')}
        >
          <span>{formatDate(invoice.invoiceDate, locale)}</span>
        </Row>
      )}

      <Row
        icon={<CalendarIcon className={fieldIconClasses} aria-hidden="true" />}
        label={t('profile.fee.dueDate')}
      >
        <span>{formatDate(invoice.dueDate, locale)}</span>
      </Row>

      <Row
        icon={<CheckIcon className={fieldIconClasses} aria-hidden="true" />}
        label={t('profile.fee.status')}
      >
        <span className={statusClass}>{statusLabel}</span>
      </Row>

      {/* The proto renders Laskutustapa as the latest invoice's last row, with
          its switch affordance inline on the right. Passed in (see the prop
          doc): the VALUE is envelope-level member data, the PLACEMENT is the
          proto's. */}
      {deliveryMethod !== undefined && (
        <DeliveryMethodRow
          label={deliveryMethod.label}
          action={deliveryMethod.action ?? null}
          labelRef={deliveryMethod.labelRef}
        />
      )}
    </dl>
  );
}

/** The Laskutustapa row: the member's delivery method, with the switch affordance inline. */
function DeliveryMethodRow({
  label,
  action,
  labelRef,
}: {
  readonly label: string;
  readonly action: JSX.Element | null;
  readonly labelRef?: Ref<HTMLSpanElement> | undefined;
}): JSX.Element {
  const t = useTranslate();
  return (
    <Row
      icon={<CreditCardIcon className={fieldIconClasses} aria-hidden="true" />}
      label={t('profile.fee.billingMethod')}
    >
      <span className="flex items-center justify-between gap-3">
        <span
          ref={labelRef}
          tabIndex={labelRef === undefined ? undefined : -1}
          className="focus-visible:rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          {label}
        </span>
        {action}
      </span>
    </Row>
  );
}

/**
 * The Laskutustapa row on its own, for a member with NO invoice card to host it
 * (048, spec edge case: the billing method and the request option apply
 * independently of whether an invoice exists). It renders the SAME
 * `DeliveryMethodRow` as the card's last row, so the two cannot drift apart.
 */
export function DeliveryMethodCard({
  label,
  action,
  labelRef,
}: {
  readonly label: string;
  readonly action?: JSX.Element | null;
  readonly labelRef?: Ref<HTMLSpanElement>;
}): JSX.Element {
  return (
    <dl className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card">
      <DeliveryMethodRow label={label} action={action ?? null} labelRef={labelRef} />
    </dl>
  );
}
